import type { z } from "zod";

import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import {
  assetSchema,
  assetSearchResponseSchema,
  cusipResponseSchema,
} from "./assets.schema";

export type Asset = z.infer<typeof assetSchema>;

export type AssetAttribute =
  | "ptp_no_exception"
  | "ptp_with_exception"
  | "ipo"
  | "has_options"
  | "options_late_close"
  | "overnight_tradable"
  | "overnight_halted";

export type AssetStatus = "active" | "inactive";

export type AssetExchange =
  | "AMEX"
  | "ARCA"
  | "BATS"
  | "NASDAQ"
  | "NYSE"
  | "OTC"
  | "CRYPTO"
  | "ERSX"
  | "FTXU"
  | "CRXL"
  | "ASCX"
  | "SWEEP";

export type AssetClass =
  // Classes we typically allow searching for trading
  | "us_equity"
  | "crypto"
  // These are not searchable for trade, or have no assets as of the date this was written
  | "us_option"
  | "crypto_perp"
  | "sweep"
  | "treasury"
  | "corporate"
  | "global_equity"
  | "fixed_income"
  | "currency";

export type AssetSearchResponse = z.infer<typeof assetSearchResponseSchema>;

// fetch the asset list
export const getAssets = (): Promise<Asset[]> =>
  instance
    .get<Asset[]>("/assets")
    .then(({ data }) =>
      looseParse(looseArray(assetSchema, "getAssets"), data, "getAssets"),
    );

// fetch the asset list
export const getAsset = async (symbol: string): Promise<Asset> => {
  const res = await instance.get<Asset>(`/assets/${symbol}`);
  return looseParse(assetSchema, res.data, "getAsset");
};

export const getCusipFromSymbol = async (symbol: string) => {
  const res = await instance.get<{
    cusip: string;
  }>(`/assets/${symbol}/cusip`);
  return looseParse(cusipResponseSchema, res.data, "getCusipFromSymbol");
};

export const getAssetsViaFullTextSearch = async (
  query: string,
  limit: number = 10,
  offset: number = 0,
  status: AssetStatus = "active",
  assetClass?: AssetClass,
  exchanges?: AssetExchange[],
): Promise<AssetSearchResponse> => {
  const res = await instance.get<AssetSearchResponse>(`/assets/search`, {
    params: {
      query,
      limit,
      offset,
      ...(status && { status }),
      ...(assetClass && { asset_class: assetClass }),
      ...(exchanges && exchanges.length > 0 && { exchange: exchanges }),
    },
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            searchParams.append(key, value.join(","));
          }
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  });
  return looseParse(
    assetSearchResponseSchema,
    res.data,
    "getAssetsViaFullTextSearch",
  );
};
